import { useEffect, useRef, useState } from 'react';

import { useLocalSearchParams, usePathname, useRouter, useSegments } from 'expo-router';
import { useStoreContext } from 'features/store';

import { appendObjectToQueryString } from 'navigation/utils';
import { routes } from 'utils/routing';

/** If no store is selected...
 * pops a store locator 1 time per screen mount, if you close it, navigate elsewhere and come back it will pop again 1 time.
 * Transparent drawer overlays (hamburger menu, notification inbox) do not count as navigating away. */
export const useStoreRequired = (isRequired = true) => {
  const { noStoreSelected } = useStoreContext();
  const pathname = usePathname();
  const segments = useSegments();
  const params = useLocalSearchParams();
  const router = useRouter();
  const didPopModalRef = useRef(false);
  const thisPathnameRef = useRef(pathname);

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    // to allow this to run in _layout files which run before normal react code and causes errors without this.
    setIsMounted(true);
  }, []);

  useEffect(() => {
    // Drawer routes (account / notification-inbox / support / …) are transparent overlays over the
    // still-mounted tab. Treat them as staying on the same tab so the one-shot latch is preserved.
    const isDrawerOverlay = segments[0] === '(drawer)';
    const didNavigateAway =
      !isDrawerOverlay &&
      !pathname?.startsWith(routes.storeLocator) &&
      !pathname.startsWith(thisPathnameRef.current);

    if (didNavigateAway) {
      // another screen has been pushed. unfortunately the cleanup of useFocusEffect isn't working reliably for this
      didPopModalRef.current = false;
    }
  }, [pathname, segments]);

  useEffect(() => {
    if (!isMounted || !isRequired) {
      return;
    }

    const isSelectingStore = pathname.startsWith(routes.storeLocator);
    if (
      noStoreSelected &&
      !isSelectingStore &&
      !didPopModalRef.current &&
      pathname.startsWith(thisPathnameRef.current)
    ) {
      didPopModalRef.current = true;
      router.navigate(
        appendObjectToQueryString(routes.storeLocator, {
          ...params,
          redirectUrl: thisPathnameRef.current,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, isRequired, noStoreSelected, params, pathname, router]);

  return { noStoreSelected };
};
